import type { DataTableAction } from '@/components/ui/data-table-customized'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import useAuthStore from '@/stores/useAuthStore'
import { useTranslation } from 'react-i18next'
import { cancelContractProcess, archiveContractProcess } from '../../services'
import { ContractProcess } from '../../types'

/** Hook returning row actions (resolve, cancel, archive) for process table */
export const useProcessActions = () => {
    const { t } = useTranslation()
    const { setModal } = useModal()
    const { setConfirmation } = useConfirmation()
    const { employee } = useAuthStore()

    const { refetch } = useQueryClientCallback({
        pattern: 'processes',
        mode: 'contains',
    })

    const { mutateAsync: cancelMutation } = useMutationWithHandler(
        { mutationFn: cancelContractProcess },
        {
            successMessage: t('process-cancelled-success'),
            reactQueryKeys: QUERY_KEYS.PROCESSES,
            onSuccess: () => refetch(),
        }
    )

    const { mutateAsync: archiveMutation } = useMutationWithHandler(
        { mutationFn: archiveContractProcess },
        {
            successMessage: t('process-archived-success'),
            reactQueryKeys: QUERY_KEYS.PROCESSES,
            onSuccess: () => refetch(),
        }
    )

    const actions: DataTableAction<ContractProcess>[] = [
        {
            key: 'resolve',
            label: t('contract-process-resolve'),
            onClick: (row) =>
                setModal('ContractModals/ResolveModal/index', {
                    contractID: row.contract_processable_id,
                    contractProcessID: row.contract_process_id,
                }),
            disabled: (row) =>
                !(
                    ['in-progress', 'pending'].includes(
                        row.contract_process_status
                    ) &&
                    row.contract_process_assignees?.includes(
                        employee?.employee_id as number
                    )
                ),
        },
        {
            key: 'cancel',
            label: t('process-cancelled'),
            onClick: (row) =>
                setConfirmation({
                    open: true,
                    title: t('process-cancelled'),
                    description: t('process-cancelled-description'),
                    onConfirm: () =>
                        cancelMutation({
                            contractID: row.contract_processable_id,
                            contractProcessID: row.contract_process_id,
                        }),
                }),
            disabled: (row) =>
                !(
                    employee?.employee_id ===
                        row.contract_process_employee_id &&
                    ['in-progress', 'pending'].includes(
                        row.contract_process_status
                    )
                ),
        },
        {
            key: 'archive',
            label: t('process-archive'),
            onClick: (row) =>
                setConfirmation({
                    open: true,
                    title: t('process-archive'),
                    description: t('process-archive-description'),
                    onConfirm: () =>
                        archiveMutation({
                            contractID: row.contract_processable_id,
                            contractProcessID: row.contract_process_id,
                        }),
                }),
            disabled: (row) =>
                !(row.contract_process_status === 'completed' && !row.archived),
        },
    ]

    return { actions }
}
